import React from 'react';
import { CONFORMITY_VALUES, type ConformityValue } from '@troia-v3/types';

import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';

/**
 * Metadados dos 3 valores de `CONFORMITY_VALUES` (@troia-v3/types).
 *
 * As cores vêm dos tokens semânticos (`--theme-success-*`/`--theme-error-*`/
 * `--theme-surface-sunken`), NUNCA de hex hardcoded — mesma paleta do
 * `StatusPill` usado no read-only da `ChecklistReviewPage`, então o campo
 * preenchido no campo e o campo revisado no escritório falam a mesma língua
 * visual.
 */
const OPTION_META: Record<
  ConformityValue,
  { label: string; icon: IconName; solidBg: string; solidFg: string; subtleBg: string; subtleFg: string }
> = {
  conforme: {
    label: 'Conforme',
    icon: 'check-circle',
    solidBg: 'var(--theme-success-color)',
    solidFg: '#ffffff',
    subtleBg: 'var(--theme-success-subtle)',
    subtleFg: 'var(--theme-success-fg)',
  },
  nao_conforme: {
    label: 'Não conforme',
    icon: 'x-circle',
    solidBg: 'var(--theme-error-color)',
    solidFg: '#ffffff',
    subtleBg: 'var(--theme-error-subtle)',
    subtleFg: 'var(--theme-error-fg)',
  },
  na: {
    label: 'N.A.',
    icon: 'minus',
    solidBg: 'var(--theme-muted-text)',
    solidFg: '#ffffff',
    subtleBg: 'var(--theme-surface-sunken)',
    subtleFg: 'var(--theme-muted-text)',
  },
};

export interface ConformityFieldProps {
  /** Id do campo do `templateSnapshot` — compõe o `data-testid` de cada botão. */
  fieldId: string;
  /** Valor atual (`conforme` | `nao_conforme` | `na`) ou `null` quando não respondido. */
  value: string | null;
  onChange: (value: ConformityValue) => void;
  disabled?: boolean;
}

/**
 * ConformityField — controle primário do checklist em campo.
 *
 * 3 botões grandes lado a lado (grid-cols-3, altura mínima 56px — bem acima do
 * alvo de 44px do iOS HIG, porque o gerente responde de pé, uma mão só, luva ou
 * sol na tela). Um toque = uma resposta: é o que garante o "zero digitação no
 * caminho feliz" da spec §7.
 *
 * Selecionado = fundo SÓLIDO na cor semântica (legível de longe, à prova de
 * dúvida sobre qual opção está marcada); não selecionado = fundo `subtle` da
 * mesma família, então a cor já antecipa o significado antes do toque.
 */
export const ConformityField: React.FC<ConformityFieldProps> = ({ fieldId, value, onChange, disabled = false }) => (
  <div className="grid grid-cols-3 gap-2" data-testid={`conformity-${fieldId}`}>
    {CONFORMITY_VALUES.map((option) => {
      const meta = OPTION_META[option];
      const selected = value === option;

      return (
        <button
          key={option}
          type="button"
          disabled={disabled}
          onClick={() => onChange(option)}
          aria-pressed={selected}
          className="flex flex-col items-center justify-center gap-1 rounded-xl px-2 py-2 text-[13px] font-semibold leading-tight text-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring-color)]"
          style={{
            minHeight: 56,
            backgroundColor: selected ? meta.solidBg : meta.subtleBg,
            color: selected ? meta.solidFg : meta.subtleFg,
            border: `1.5px solid ${selected ? meta.solidBg : 'transparent'}`,
          }}
          data-testid={`conformity-${fieldId}-${option}`}
        >
          <Icon name={meta.icon} className="w-5 h-5 flex-none" />
          <span className="truncate max-w-full">{meta.label}</span>
        </button>
      );
    })}
  </div>
);

ConformityField.displayName = 'ConformityField';
